import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Receipt, FileSpreadsheet,
  GitCompareArrows, PieChart, KeyRound, LogOut, Wallet,
  Users, Settings, ChevronDown, Archive, MailQuestion,
  Sparkles, Car, Mail, FileSignature,
} from "lucide-react";

const nav = [
  { to: "/app", icon: LayoutDashboard, label: "Übersicht", exact: true, testid: "nav-dashboard", perm: null },
  { to: "/app/firmen", icon: Building2, label: "Firmen", testid: "nav-companies", perm: "receipt.read" },
  { to: "/app/kontoauszuege", icon: FileSpreadsheet, label: "Kontoauszüge", testid: "nav-statements", perm: "statement.read" },
  { to: "/app/belege", icon: Receipt, label: "Belege", testid: "nav-receipts", perm: "receipt.read" },
  { to: "/app/vertraege", icon: FileSignature, label: "Verträge", testid: "nav-contracts", perm: "receipt.read" },
  { to: "/app/fehlende-belege", icon: MailQuestion, label: "Fehlende Belege", testid: "nav-missing", perm: "receipt.request" },
  { to: "/app/fahrzeuge", icon: Car, label: "Fahrzeuge", testid: "nav-vehicles", perm: "receipt.request" },
  { to: "/app/archiv", icon: Archive, label: "Rechnungsarchiv", testid: "nav-archive", perm: "receipt.read" },
  { to: "/app/zuordnung", icon: GitCompareArrows, label: "Zuordnung", testid: "nav-matching", perm: "match" },
  { to: "/app/auswertung", icon: PieChart, label: "Auswertung", testid: "nav-analytics", perm: "statement.read" },
  { to: "/app/einstellungen", icon: Settings, label: "Einstellungen", testid: "nav-settings", perm: "settings.manage" },
];

const roleLabels = { owner: "Inhaber", accountant: "Buchhalter", entry: "Erfasser" };
const roleBadge = { owner: "bg-[#E4F0E8] text-[#18322B]", accountant: "bg-[#EAF4FA] text-[#1A4971]", entry: "bg-[#FFF4E5] text-[#915000]" };

export default function Layout() {
  const { user, logout, workspaces, activeWorkspaceId, setActiveWorkspaceId, role, has } = useAuth();
  const navigate = useNavigate();
  const [wsOpen, setWsOpen] = React.useState(false);

  const activeWs = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const switchWs = (id) => {
    setActiveWorkspaceId(id);
    setWsOpen(false);
    window.location.reload(); // reload data for new workspace
  };

  return (
    <div className="min-h-screen flex bg-[#F9F9F8]" data-testid="app-shell">
      <aside className="w-64 shrink-0 border-r border-[#E6E6E4] bg-white flex flex-col">
        <div className="px-6 py-6 border-b border-[#E6E6E4]">
          <div className="flex items-center">
            <img
              src="/vorbuch-logo.png"
              alt="vorbuch.de"
              className="h-11 w-auto"
              data-testid="app-logo"
            />
          </div>
        </div>

        {workspaces.length > 1 && (
          <div className="px-4 pt-4 relative">
            <button
              onClick={() => setWsOpen(!wsOpen)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-[#E6E6E4] hover:bg-[#F3F3F1] transition-colors duration-150 text-left"
              data-testid="workspace-switcher"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Arbeitsbereich</div>
                <div className="text-xs text-[#1A1A1A] truncate">{activeWs?.name || "—"}</div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-[#5C5E60]" />
            </button>
            {wsOpen && (
              <div className="absolute left-4 right-4 mt-1 z-20 bg-white border border-[#E6E6E4] rounded-md shadow-md">
                {workspaces.map((w) => (
                  <button key={w.id} onClick={() => switchWs(w.id)}
                    data-testid={`workspace-${w.id}`}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[#F3F3F1] transition-colors ${w.id === activeWorkspaceId ? "font-semibold text-[#18322B]" : "text-[#1A1A1A]"}`}>
                    <div className="truncate">{w.name}</div>
                    <div className="text-[10px] text-[#5C5E60]">{roleLabels[w.role] || w.role}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {nav.map((item) => {
            if (item.perm && !has(item.perm)) return null;
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.exact} data-testid={item.testid}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                    isActive
                      ? "bg-[#E4F0E8] text-[#18322B] font-semibold"
                      : "text-[#5C5E60] hover:bg-[#F3F3F1] hover:text-[#1A1A1A]",
                  ].join(" ")
                }>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#E6E6E4] p-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#A0A0A0] mb-2">Angemeldet</div>
          <div className="flex items-center gap-2 mb-1">
            <div className="text-sm font-medium text-[#1A1A1A] truncate flex-1">{user?.name || user?.email}</div>
            {role && (
              <span className={`inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${roleBadge[role] || "bg-[#F3F3F1] text-[#5C5E60]"}`}
                data-testid="user-role-badge">
                {roleLabels[role] || role}
              </span>
            )}
          </div>
          <div className="text-xs text-[#5C5E60] truncate">{user?.email}</div>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-md border border-[#E6E6E4] hover:bg-[#F3F3F1] transition-colors duration-150">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
